import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ChatTeardropDots, Bug, Lightbulb, Heart, Star, X } from '@phosphor-icons/react';

const CATEGORIES = [
  { id: 'bug',     label: 'Report a Bug',       icon: Bug,       color: '#C53030' },
  { id: 'feature', label: 'Suggest a Feature',  icon: Lightbulb, color: '#C5A059' },
  { id: 'praise',  label: 'Share What You Love', icon: Heart,    color: '#2D6A4F' },
  { id: 'general', label: 'General Feedback',   icon: ChatTeardropDots, color: '#0A1128' },
];

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setCategory('general');
      setRating(0);
      setMessage('');
    }, 300);
  };

  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 3) {
      toast.error('Please share a few words of feedback');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/feedback', {
        category,
        rating: rating > 0 ? rating : null,
        message: trimmed,
        page: location.pathname,
      });
      toast.success('Thank you — your feedback helps shape the beta');
      reset();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-widget-open-button"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#C5A059] hover:bg-white/5 rounded-md transition-colors border border-[#C5A059]/30"
      >
        <ChatTeardropDots size={16} weight="duotone" />
        Share Beta Feedback
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 fade-in"
          onClick={reset}
          data-testid="feedback-widget-modal"
        >
          <div
            className="bg-white rounded-md shadow-2xl max-w-md w-full overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0A1128] p-5 relative">
              <button
                onClick={reset}
                data-testid="feedback-widget-close-button"
                className="absolute top-4 right-4 text-white/70 hover:text-white"
              >
                <X size={18} />
              </button>
              <p className="text-xs uppercase tracking-[0.15em] text-[#C5A059]">Beta Feedback</p>
              <h2 className="cabinet-grotesk text-xl font-medium text-white mt-1">
                Help shape PLANIVANCE AI
              </h2>
              <p className="text-xs text-[#A0AABF] mt-1">
                Your input goes directly to the founders. We read every note.
              </p>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <Label className="text-xs text-[#4A5568]">Category</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const active = category === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => setCategory(c.id)}
                        data-testid={`feedback-category-${c.id}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-all border ${
                          active
                            ? 'bg-[#0A1128] text-white border-[#0A1128]'
                            : 'bg-white text-[#4A5568] border-slate-200 hover:border-[#C5A059]'
                        }`}
                      >
                        <Icon size={14} weight="duotone" style={{ color: active ? '#C5A059' : c.color }} />
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#4A5568]">Overall rating (optional)</Label>
                <div className="flex gap-1 mt-2" data-testid="feedback-rating-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setRating(rating === n ? 0 : n)}
                      data-testid={`feedback-star-${n}`}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        size={20}
                        weight={n <= rating ? 'fill' : 'regular'}
                        className={n <= rating ? 'text-[#C5A059]' : 'text-slate-300'}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#4A5568]">Your feedback</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  data-testid="feedback-message-input"
                  placeholder="What worked well? What's confusing? What would you love to see next?"
                  rows={5}
                  maxLength={4000}
                  className="mt-1 border-slate-300 resize-none"
                />
                <p className="text-[10px] text-[#A0AABF] mt-1 text-right">{message.length}/4000</p>
              </div>

              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="feedback-submit-button"
                className="w-full bg-[#0A1128] hover:bg-[#152145]"
              >
                {submitting ? 'Sending...' : 'Send Feedback'}
              </Button>

              <p className="text-[10px] text-[#A0AABF] leading-relaxed">
                Your email and the page you're on are included automatically so we can follow up. No financial data is shared.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
